import {
    useState,
    useContext,
    createContext,
    useEffect,
    useCallback
} from "react";
import Amplitude from "../../../services/amplitude";
import ExperienceModes from "../../guest/ExperienceModes";
import { useTranslation } from "react-i18next";

const FormsConfigContext = createContext({ templatePreferences: {} });

export function FormsConfigProvider({
    formConfig,
    formDataLists,
    formsPreferences,
    guestMode,
    modalContainerRef,
    children
}) {
    const [translate] = useTranslation();
    const [config, setConfig] = useState(formConfig);
    const [dataLists, setDataLists] = useState(formDataLists);
    const [formTemplate, setFormTemplate] = useState(null);
    // Preferences defined for this current template - higher priority
    const [templatePreferences, setTemplatePreferences] = useState({});
    // Preferences for all forms templates - lower priority
    const [preferences, setPreferences] = useState();
    const [experienceMode, setExperienceMode] = useState(
        ExperienceModes.HOME_PAGE
    );

    useEffect(() => {
        setPreferences(formsPreferences);
    }, [formsPreferences, setPreferences]);

    useEffect(() => {
        if (!config) {
            setConfig(formConfig);
        }
    }, [config, formConfig]);

    useEffect(() => {
        setDataLists(formDataLists);
    }, [dataLists, formDataLists]);

    useEffect(() => {
        if (formTemplate) {
            setTemplatePreferences(formTemplate.preferences || {});
        }
    }, [formTemplate]);

    const logAmplitudeEvent = useCallback(
        ({ eventType, eventProperties }) => {
            if (guestMode) {
                Amplitude.logAmplitute({
                    eventType,
                    guestSession: formTemplate.guestSessionId,
                    eventProperties
                });
            }
        },
        [guestMode, formTemplate]
    );

    const isSectionUsedInCondition = useCallback(({ sectionId, condition }) => {
        if (!condition) {
            return false;
        }

        for (var i = 0; i < condition.length; i += 1) {
            for (var j = 0; j < condition[i].length; j += 1) {
                if (condition[i][j].section === sectionId) {
                    return true;
                }
            }
        }
        return false;
    }, []);

    const isItemUsedInCondition = useCallback(({ itemId, condition }) => {
        if (!condition) {
            return false;
        }

        for (var i = 0; i < condition.length; i += 1) {
            for (var j = 0; j < condition[i].length; j += 1) {
                if (condition[i][j].item === itemId) {
                    return true;
                }
            }
        }
        return false;
    }, []);

    const isItemUsedInFormConditions = useCallback(
        ({ itemId }) => {
            if (!formTemplate) {
                return {
                    isUsed: false,
                    message: ""
                };
            }
            let isUsed = false;
            let whereUsed = {};

            for (var i = 0; i < formTemplate.sections.length; i += 1) {
                const section = formTemplate.sections[i];

                whereUsed[section.name] = {
                    isSection: false,
                    items: []
                };

                if (
                    isItemUsedInCondition({
                        itemId,
                        condition: section.readOnly
                    })
                ) {
                    whereUsed[section.name].isUsed = translate(
                        "OTM_DELTE_ITEM_CONDITION_READONLY"
                    );
                    isUsed = true;
                }

                if (
                    isItemUsedInCondition({
                        itemId,
                        condition: section.visible
                    })
                ) {
                    whereUsed[section.name].isUsed = translate(
                        "OTM_DELTE_ITEM_CONDITION_VISIBLE"
                    );
                    isUsed = true;
                }

                if (section.items) {
                    for (var j = 0; j < section.items.length; j++) {
                        if (section.items[j]._id !== itemId) {
                            if (
                                isItemUsedInCondition({
                                    itemId,
                                    condition: section.items[j].readOnly
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_READONLY",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                            if (
                                isItemUsedInCondition({
                                    itemId,
                                    condition: section.items[j].visible
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_VISIBLE",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                            if (
                                isItemUsedInCondition({
                                    itemId,
                                    condition: section.items[j].validation
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_VALIDATION",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                        }
                    }
                }
            }

            return {
                isUsed,
                whereUsed
            };
        },
        [formTemplate, isItemUsedInCondition, translate]
    );

    const isSectionUsedInFormConditions = useCallback(
        ({ sectionId }) => {
            if (!formTemplate) {
                return {
                    isUsed: false,
                    message: ""
                };
            }
            let isUsed = false;
            let whereUsed = {};

            for (var i = 0; i < formTemplate.sections.length; i += 1) {
                const section = formTemplate.sections[i];
                if (section._id !== sectionId) {
                    whereUsed[section.name] = {
                        isSection: false,
                        items: []
                    };
                    if (
                        isSectionUsedInCondition({
                            sectionId,
                            condition: section.readOnly
                        })
                    ) {
                        whereUsed[section.name].isUsed = translate(
                            "OTM_DELTE_ITEM_CONDITION_READONLY"
                        );
                        isUsed = true;
                    }

                    if (
                        isSectionUsedInCondition({
                            sectionId,
                            condition: section.visible
                        })
                    ) {
                        whereUsed[section.name].isUsed = translate(
                            "OTM_DELTE_ITEM_CONDITION_VISIBLE"
                        );
                        isUsed = true;
                    }

                    if (section.items) {
                        for (var j = 0; j < section.items.length; j++) {
                            if (
                                isSectionUsedInCondition({
                                    sectionId,
                                    condition: section.items[j].readOnly
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_READONLY",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                            if (
                                isSectionUsedInCondition({
                                    sectionId,
                                    condition: section.items[j].visible
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_VISIBLE",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                            if (
                                isSectionUsedInCondition({
                                    sectionId,
                                    condition: section.items[j].validation
                                })
                            ) {
                                whereUsed[section.name].items.push(
                                    translate(
                                        "OTM_DELTE_ITEM_CHECK_ITEM_USED_IN_VALIDATION",
                                        { name: section.items[j].name }
                                    )
                                );
                                isUsed = true;
                            }
                        }
                    }
                }
            }

            return {
                isUsed,
                whereUsed
            };
        },
        [formTemplate, isSectionUsedInCondition, translate]
    );

    return (
        <FormsConfigContext.Provider
            value={{
                dataTypes: config?.dataTypes,
                dataTypesWithOperationsList: config?.dataTypesWithOperationsList || {},
                dataListsColorOptions: config?.dataListsColorOptions,
                labelGroups: config?.labelGroups,
                defaultBackgroundColor: config?.defaultBackgroundColor,
                formTemplate,
                templatePreferences,
                setFormTemplate,
                dataLists,
                guestMode,
                modalContainerRef,
                logAmplitudeEvent,
                experienceMode,
                setExperienceMode,
                isItemUsedInFormConditions,
                isSectionUsedInFormConditions,
                preferences,
                COLUMNS_OPTIONS: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((col) => ({ _id: col, name: col })),
                DECIMALS_OPTIONS: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(o => ({ _id: o, name: o.toString() })),
                LABEL_FONT_SIZE_OPTIONS: [1, 2, 3, 4, 5, 6].map((fs) => ({ _id: fs, name: fs }))
            }}
        >
            {children}
        </FormsConfigContext.Provider>
    );
}

export function useFormsConfig() {
    return useContext(FormsConfigContext);
}
